/**
 * useConfigError Hook
 * Phase 1 Day 4 - Configuration error state management
 *
 * Manages configuration errors that occur during app initialization or runtime.
 * Provides a simple state container with helper functions for error handling.
 *
 * @returns {Object} Configuration error state and management functions
 */

import { useState } from "react";
import logger from "../utils/logger";

export const useConfigError = () => {
  const [configError, setConfigError] = useState(null);

  /**
   * Set a configuration error
   * @param {string|Object} error - The error message or object
   */
  const setError = (error) => {
    logger.log(`[useConfigError] Setting error:`, error);
    setConfigError(error);
  };

  /**
   * Clear the configuration error
   */
  const clearError = () => {
    logger.log("[useConfigError] Clearing error");
    setConfigError(null);
  };

  /**
   * Check if there is an active configuration error
   * @returns {boolean} True if error exists
   */
  const hasError = () => {
    return configError !== null;
  };

  /**
   * Get error message as string
   * @returns {string} The error message
   */
  const getErrorMessage = () => {
    if (!configError) return "";
    if (typeof configError === "string") return configError;
    return configError.message || String(configError);
  };

  return {
    // State
    configError,

    // Actions
    setConfigError, // Backward compatibility
    setError,
    clearError,

    // Utilities
    hasError,
    getErrorMessage,
  };
};
